import type {ChatService, ChatServiceParams, CrosstabsClassificationData, CrosstabsObjectivesData, CrosstabsOptionsData, CrosstabsFileRequestData, CrosstabsPlanData, CrosstabsProgressData, DiscoveryQuestionData, IntentResult, McpAppData, McpAppInputData, McpToolResultData, SiteBuilderCodeChunkData, SiteBuilderOptionsData, SiteBuilderPlanData, ProgressStepData, ProgressStepUpdateData, AuthoringIterationData, AuthoringVerifyData, LogData} from '../components/R3Chat/types'

/**
 * WebChatService
 *
 * Implements ChatService for the web app using fetch + ReadableStream
 * to consume the SSE stream from POST /api/r3/chat.
 */
export class WebChatService implements ChatService {
  private baseUrl: string
  private userEmail: string | undefined
  private accessToken: string | undefined
  private abortController: AbortController | null = null

  constructor(baseUrl: string, userEmail?: string, accessToken?: string) {
    this.baseUrl = baseUrl
    this.userEmail = userEmail
    this.accessToken = accessToken
  }

  /** Update the access token (e.g. after Cognito refresh) */
  setAccessToken(token: string): void {
    this.accessToken = token
  }

  /** Cancel the current in-flight request */
  cancel(): void {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  async sendMessage(params: ChatServiceParams): Promise<void> {
    const {message, sessionId, department, history, attachedFileIds, onChunk, onIntent, onSiteBuilderOptions, onSiteBuilderPlan, onSiteBuilderGenerating, onSiteBuilderCodeChunk, onSiteBuilderPreview, onSiteBuilderPublished, onDiscoveryQuestion, onCrosstabsProgress, onCrosstabsClassification, onCrosstabsObjectives, onCrosstabsPlan, onCrosstabsOptions, onCrosstabsFileRequest, onMcpApp, onMcpAppInput, onMcpToolResult, onAuthoringStep, onAuthoringStepUpdate, onAuthoringIteration, onAuthoringVerify, onLog, onHandoff, onComplete, onError} = params

    // Create a new AbortController for this request
    this.abortController = new AbortController()
    const {signal} = this.abortController

    const headers: Record<string, string> = {'Content-Type': 'application/json'}
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`
    }
    if (this.userEmail) {
      headers['x-user-email'] = this.userEmail
    }

    let response: Response

    try {
      response = await fetch(`${this.baseUrl}/api/r3/chat`, {
        method: 'POST',
        credentials: 'include',
        headers,
        signal,
        body: JSON.stringify({
          message,
          sessionId,
          department,
          conversationHistory: history,
          ...(attachedFileIds && attachedFileIds.length > 0 ? {attachedFileIds} : {})
        })
      })
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // User cancelled — not an error
        return
      }
      onError(new Error(`Network error: ${(err as Error).message}`))
      return
    }

    if (!response.ok) {
      onError(new Error(`Server error: ${response.status} ${response.statusText}`))
      return
    }

    if (!response.body) {
      onError(new Error('No response body'))
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let returnedSessionId = sessionId ?? ''

    try {
      while (true) {
        let readResult: ReadableStreamReadResult<Uint8Array>
        try {
          readResult = await reader.read()
        } catch (err) {
          if ((err as Error).name === 'AbortError' || signal.aborted) {
            // User cancelled — stop reading, not an error
            break
          }
          throw err
        }
        const {done, value} = readResult
        if (done) break

        buffer += decoder.decode(value, {stream: true})

        // SSE events are separated by double newlines
        const events = buffer.split('\n\n')
        buffer = events.pop() ?? ''

        for (const rawEvent of events) {
          if (!rawEvent.trim()) continue

          const lines = rawEvent.split('\n')
          let eventType = ''
          let dataStr = ''

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim()
            } else if (line.startsWith('data: ')) {
              dataStr = line.slice(6).trim()
            }
          }

          if (!dataStr) continue

          let data: any
          try {
            data = JSON.parse(dataStr)
          } catch {
            continue
          }

          switch (eventType) {
            case 'chunk':
              if (data.text) onChunk(data.text)
              break

            case 'intent':
              onIntent(data as IntentResult)
              break

            case 'complete':
              returnedSessionId = data.sessionId || returnedSessionId
              onComplete(returnedSessionId, data.tokenUsage, {
                contextTokens: data.contextTokens ?? 0,
                contextWindow: data.contextWindow ?? 200000,
                compacted: data.compacted ?? false
              })
              break

            case 'site_builder_options':
              if (onSiteBuilderOptions) onSiteBuilderOptions(data as SiteBuilderOptionsData)
              break

            case 'site_builder_plan':
              if (onSiteBuilderPlan) onSiteBuilderPlan(data as SiteBuilderPlanData)
              break

            case 'site_builder_generating':
              if (onSiteBuilderGenerating) onSiteBuilderGenerating(data.siteId)
              break

            case 'site_builder_code_chunk':
              if (onSiteBuilderCodeChunk) onSiteBuilderCodeChunk(data as SiteBuilderCodeChunkData)
              break

            case 'site_builder_preview':
              if (onSiteBuilderPreview) onSiteBuilderPreview(data.html, data.siteId)
              break

            case 'site_builder_published':
              if (onSiteBuilderPublished) onSiteBuilderPublished(data.url, data.siteId)
              break

            case 'discovery_question':
              if (onDiscoveryQuestion) onDiscoveryQuestion(data as DiscoveryQuestionData)
              break

            case 'crosstabs_progress':
              if (onCrosstabsProgress) onCrosstabsProgress(data as CrosstabsProgressData)
              break

            case 'crosstabs_classification':
              if (onCrosstabsClassification) onCrosstabsClassification(data as CrosstabsClassificationData)
              break

            case 'crosstabs_objectives':
              if (onCrosstabsObjectives) onCrosstabsObjectives(data as CrosstabsObjectivesData)
              break

            case 'crosstabs_plan':
              if (onCrosstabsPlan) onCrosstabsPlan(data as CrosstabsPlanData)
              break

            case 'crosstabs_options':
              if (onCrosstabsOptions) onCrosstabsOptions(data as CrosstabsOptionsData)
              break

            case 'crosstabs_file_request':
              if (onCrosstabsFileRequest) onCrosstabsFileRequest(data as CrosstabsFileRequestData)
              break

            case 'mcp_app':
              if (onMcpApp) onMcpApp(data as McpAppData)
              break

            case 'mcp_app_input':
              if (onMcpAppInput) onMcpAppInput(data as McpAppInputData)
              break

            case 'mcp_tool_result':
              if (onMcpToolResult) onMcpToolResult(data as McpToolResultData)
              break

            // ── Authoring pipeline events (CF backend) ──────────────
            case 'authoring_step':
              if (onAuthoringStep) onAuthoringStep(data as ProgressStepData)
              break

            case 'authoring_step_update':
              if (onAuthoringStepUpdate) onAuthoringStepUpdate(data as ProgressStepUpdateData)
              break

            case 'authoring_iteration':
              if (onAuthoringIteration) onAuthoringIteration(data as AuthoringIterationData)
              break

            case 'authoring_verify':
              if (onAuthoringVerify) onAuthoringVerify(data as AuthoringVerifyData)
              break

            case 'log':
              if (onLog) onLog(data as LogData)
              break

            case 'handoff':
              if (onHandoff) onHandoff(data as { from: string; to: string; reason: string; summary: string })
              break

            case 'error':
              onError(new Error(data.message || 'Unknown server error'))
              return
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError' || signal.aborted) {
        // User cancelled — not an error
        return
      }
      onError(new Error(`Stream read error: ${(err as Error).message}`))
    } finally {
      reader.releaseLock()
      this.abortController = null
    }
  }
}
